import { AttributeSpec, Mark, Node, Schema as PMSchema } from "prosemirror-model";

const textblockAttributes: Record<string, AttributeSpec> = {
  align: {
    default: "left",
    validate: (value) => {
      if (typeof value !== "string" || !/^(left|right|center)$/.test(value))
        throw new RangeError(`Invalid value for attribute align (${value})`);
    },
  },
  indent: {
    default: 0,
    validate: (value) => {
      if (typeof value !== "number" || value < 0 || Math.floor(value) !== value || value > 8)
        throw new RangeError(`Invalid value for attribute indent (${value})`);
    },
  },
};

export const schema = new PMSchema({
  nodes: {
    doc: {
      content: "block+",
    },
    paragraph: {
      content: "inline*",
      attrs: textblockAttributes,
      toDOM: (node) => ["p", domAttributes(node), 0],
      group: "block",
      parseDOM: [
        { tag: "li > p", skip: true, priority: 60 },
        { tag: "p", getAttrs: parseAttributes },
      ],
    },
    bullet_list_item: {
      content: "inline*",
      attrs: textblockAttributes,
      toDOM: (node) => ["li", domAttributes(node, "bullet-list"), 0],
      parseDOM: [
        { tag: "li.bullet-list", getAttrs: parseAttributes },
        {
          tag: "ul > li",
          priority: 40,
          getAttrs: parseExternalListAttributes,
        },
      ],
      group: "block",
      defining: true,
    },
    ordered_list_item: {
      content: "inline*",
      attrs: textblockAttributes,
      toDOM: (node) => ["li", domAttributes(node, "ordered-list"), 0],
      parseDOM: [
        { tag: "li.ordered-list", getAttrs: parseAttributes },
        {
          tag: "ol > li",
          priority: 40,
          getAttrs: parseExternalListAttributes,
        },
      ],
      group: "block",
      defining: true,
    },
    quote: {
      content: "inline*",
      attrs: textblockAttributes,
      toDOM: (node) => ["blockquote", domAttributes(node), 0],
      parseDOM: [{ tag: "blockquote", getAttrs: parseAttributes }],
      group: "block",
      defining: true,
    },
    code_block: {
      content: "inline*",
      attrs: textblockAttributes,
      toDOM: (node) => ["pre", domAttributes(node, "code-block"), 0],
      parseDOM: [{ tag: "pre.code-block", getAttrs: parseAttributes }],
      group: "block",
      defining: true,
      code: true, // no smart quotes in code blocks
    },
    heading: {
      content: "inline*",
      attrs: {
        ...textblockAttributes,
        level: {
          default: 1,
          validate: (value) => {
            if (typeof value !== "number" || value < 1 || Math.floor(value) !== value || value > 6)
              throw new RangeError(`Invalid value for attribute level (${value})`);
          },
        },
      },
      toDOM: (node) => [`h${node.attrs.level}`, domAttributes(node), 0],
      parseDOM: [
        { tag: "h1", getAttrs: parseHeadingAttributes(1) },
        { tag: "h2", getAttrs: parseHeadingAttributes(2) },
        { tag: "h3", getAttrs: parseHeadingAttributes(3) },
        { tag: "h4", getAttrs: parseHeadingAttributes(4) },
        { tag: "h5", getAttrs: parseHeadingAttributes(5) },
        { tag: "h6", getAttrs: parseHeadingAttributes(6) },
      ],
      group: "block",
      defining: true,
    },
    image: {
      inline: true,
      group: "inline",
      attrs: {
        src: { validate: "string" },
        width: { default: null, validate: "number|null" },
        alt: { default: null, validate: "string|null" },
        isDecorative: { default: null, validate: "boolean|null" },
      },
      toDOM: (node) => [
        "img",
        {
          src: node.attrs.src,
          style: node.attrs.width ? `width: ${node.attrs.width}px` : null,
          alt: node.attrs.isDecorative ? "" : node.attrs.alt,
          class: "inline-block align-bottom",
          "data-decorative": node.attrs.isDecorative ? "true" : null,
          // If the image is decorative, we store the alt text in a data attribute so that it can be restored if the image is later changed to non-decorative.
          "data-alt": node.attrs.isDecorative ? node.attrs.alt : null,
        },
      ],
      parseDOM: [
        {
          tag: "img[src]",
          getAttrs: (elt: HTMLElement) => ({
            src: (elt as HTMLImageElement).src,
            width: elt.style.width ? parseInt(elt.style.width, 10) : null,
            alt:
              elt.getAttribute("data-decorative") === "true"
                ? elt.getAttribute("data-alt")
                : (elt as HTMLImageElement).alt || null,
            isDecorative: elt.getAttribute("data-decorative") === "true" ? true : null,
          }),
        },
      ],
    },
    formula: {
      inline: true,
      group: "inline",
      attrs: {
        formula: { validate: "string" },
      },
      toDOM: (node) => ["span", { class: "formula" }, node.attrs.formula],
      parseDOM: [
        {
          tag: "span.formula",
          getAttrs: (elt) => ({ formula: elt.textContent }),
        },
      ],
    },
    text: {
      inline: true,
      group: "inline",
    },
  },
  marks: {
    link: {
      attrs: {
        href: { validate: "string" },
      },
      inclusive: false,
      parseDOM: [
        {
          tag: "a[href]",
          getAttrs: (dom: HTMLElement) => ({ href: (dom as HTMLLinkElement).href }),
        },
      ],
      toDOM: (node) => [
        "a",
        { href: node.attrs.href, target: "_blank", rel: "noopener noreferrer" },
        0,
      ],
    },
    color: {
      attrs: {
        color: {
          validate: (value) => {
            if (typeof value !== "string" || !/^#[\da-f]{6}$/i.test(value))
              throw new RangeError(`Invalid value for attribute color (${value})`);
          },
        },
      },
      toDOM: (node) => ["span", { style: `color: ${node.attrs.color}` }, 0],
      parseDOM: [{ tag: "span", getAttrs: parseColorAttributes }],
    },
    italic: {
      parseDOM: [
        { tag: "i" },
        { tag: "em" },
        { style: "font-style=italic" },
        { style: "font-style=normal", clearMark: (m) => m.type.name === "italic" },
      ],
      toDOM() {
        return ["em", 0];
      },
    },
    bold: {
      parseDOM: [
        { tag: "strong" },
        { tag: "b", getAttrs: (node: HTMLElement) => node.style.fontWeight !== "normal" && null },
        { style: "font-weight=400", clearMark: (m) => m.type.name === "bold" },
        {
          style: "font-weight",
          getAttrs: (value: string) => /^(bold(er)?|[5-9]\d{2,})$/.test(value) && null,
        },
      ],
      toDOM() {
        return ["strong", 0];
      },
    },
    underline: {
      parseDOM: [{ tag: "u" }, { style: "text-decoration=underline" }],
      toDOM() {
        return ["u", 0];
      },
    },
  },
});

export type Schema = typeof schema;
type ExtractSchemaNodes<T> = T extends PMSchema<infer U> ? U : never;
export type SchemaNodes = ExtractSchemaNodes<Schema>;

// Typed attribute shapes for each node and mark type in the schema.

export type AlignValue = "left" | "center" | "right";

export interface TextblockAttrs {
  align: AlignValue;
  indent: number;
}

export interface HeadingAttrs extends TextblockAttrs {
  level: number;
}

export interface ImageAttrs {
  src: string;
  width: number | null;
  alt: string | null;
  isDecorative: boolean | null;
}

export interface FormulaAttrs {
  formula: string;
}

export interface LinkMarkAttrs {
  href: string;
}

export interface ColorMarkAttrs {
  color: string;
}

// Utility types that replace the wide `attrs` on Node/Mark with a specific shape.
// Used as the narrowed type in type predicates below.
export type TypedNode<T> = Omit<Node, "attrs"> & { attrs: T };
export type TypedMark<T> = Omit<Mark, "attrs"> & { attrs: T };

/**
 * Safe accessor for shared textblock attributes (align, indent).
 * Valid on any block node — all block nodes in this schema have these attrs.
 */
export function getTextblockAttrs(node: Node): TextblockAttrs {
  return node.attrs as TextblockAttrs;
}

/** Narrows node.attrs to HeadingAttrs, giving typed access to level. */
export function isHeadingNode(node: Node): node is TypedNode<HeadingAttrs> {
  return node.type === schema.nodes.heading;
}

/** Narrows node.attrs to ImageAttrs, giving typed access to src and width. */
export function isImageNode(node: Node): node is TypedNode<ImageAttrs> {
  return node.type === schema.nodes.image;
}

/** Narrows node.attrs to FormulaAttrs, giving typed access to formula. */
export function isFormulaNode(node: Node): node is TypedNode<FormulaAttrs> {
  return node.type === schema.nodes.formula;
}

/** Narrows mark.attrs to LinkMarkAttrs, giving typed access to href. */
export function isLinkMark(mark: Mark): mark is TypedMark<LinkMarkAttrs> {
  return mark.type === schema.marks.link;
}

/** Narrows mark.attrs to ColorMarkAttrs, giving typed access to color. */
export function isColorMark(mark: Mark): mark is TypedMark<ColorMarkAttrs> {
  return mark.type === schema.marks.color;
}

function domAttributes(node: Node, baseClass?: string) {
  const cls = baseClass ? [baseClass] : [];
  const result: Record<string, string> = {};
  if (node.attrs.align === "center") cls.push("text-center");
  if (node.attrs.align === "right") cls.push("text-right");
  cls.push(`block-indent-${node.attrs.indent || 0}`);
  if (cls.length) result.class = cls.join(" ");
  return result;
}

function parseAttributes(elt: HTMLElement) {
  const attrs: Record<string, unknown> = { indent: 0, align: "left" };
  for (let i = 0; i < elt.classList.length; i += 1) {
    const cls = elt.classList[i];
    const m = /^block-indent-([1-9])$/.exec(cls);
    if (cls === "text-right") attrs.align = "right";
    else if (cls === "text-center") attrs.align = "center";
    else if (m) attrs.indent = +m[1];
  }
  return attrs;
}

function parseHeadingAttributes(level: number) {
  return (elt: HTMLElement) => {
    const attrs = parseAttributes(elt);
    attrs.level = level;
    return attrs;
  };
}

function parseExternalListAttributes(element: HTMLElement) {
  const attrs = parseAttributes(element);

  let enclosingListCount = 0;
  let node: HTMLElement | null = element.parentElement;
  while (node) {
    if (node.matches("ul, ol")) enclosingListCount += 1;
    node = node.parentElement;
  }

  // One enclosing list → top-level item (indent 0); each deeper wrapper adds 1.
  attrs.indent = Math.min(8, Math.max(0, enclosingListCount - 1));
  return attrs;
}

function parseColorAttributes(elt: HTMLElement) {
  const m = /^#([\da-f]{3,8})|^rgba?\((\d+), ?(\d+), ?(\d+)/i.exec(elt.style.color);
  if (!m) return false;
  const [, hex, red, green, blue] = m;
  let r;
  let g;
  let b;
  if (hex && hex.length >= 6) {
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  } else if (hex) {
    r = parseInt(hex[0], 16) * 17;
    g = parseInt(hex[1], 16) * 17;
    b = parseInt(hex[2], 16) * 17;
  } else {
    r = parseInt(red, 10);
    g = parseInt(green, 10);
    b = parseInt(blue, 10);
  }
  const n = (value: number) => value.toString(16).toUpperCase().padStart(2, "0");
  return { color: `#${n(r)}${n(g)}${n(b)}` };
}
